import { AssessmentStatuses } from '@schooly/constants';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { SCHOOLY_API_V2_URL } from '../config';
import {
  Assessment,
  AssessmentAction,
  AssessmentEntrySave,
  AssessmentWithEntries,
  EvaluationType,
  ListAssessmentsForSchool,
  UpsertDeleteAssessmentEntry,
} from './apiTypes/assessments';
import {
  AssessmentCreateRequest,
  AssessmentCreateResponse,
  AssessmentDeleteRequest,
  AssessmentDeleteResponse,
  AssessmentForGroupExportRequest,
  AssessmentForRelationExportRequest,
  AssessmentSingleExportRequest,
  AssessmentUpdateRequest,
  AssessmentUpdateResponse,
  CreateAssessmentGradeRequest,
  ExportMarkbookParams,
  GetAssessmentGroupsRequest,
  GetAssessmentRequest,
  GetAssessmentsAverageRequestParams,
  GetAssessmentsAverageRequestQuery,
  GetAssessmentsAverageResponse,
  GetAssessmentsEntriesParams,
  GetMarkbookParams,
  GetMarkbookResponseBody,
  GetMarkbookSort,
  GetMarkbookSortBy,
  isGetMarkbookSortWithParams,
  PerformAssessmentActionRequest,
  PerformAssessmentActionResponse,
  SelectListCreateResponse,
  SelectListDeleteResponse,
  SelectListDetailResponseBody,
  SelectListListResponseBody,
  SelectListPartialUpdateResponse,
  UpdateAssessmentGradeRequest,
} from './apiTypes/endpoints/assessments';
import { IColumnSort } from './apiTypes/endpoints/people';
import { Group, GroupType } from './apiTypes/groups';
import { ApiError, PagedResponse, SORT_DIRECTION } from './apiTypes/misc';
import {
  buildAssessmentsAveragePath,
  createGradePath,
  deleteGradePath,
  exportMarkbookPath,
  getAssessmentEntriesForSchoolPath,
  getAssessmentsListPath,
  getGradesPath,
  getMarkbookPath,
  getSingleAssessmentPath,
  getSingleGradePath,
  updateGradePath,
} from './apiTypes/paths/assessments';
import {
  RQUseInfiniteQueryOptions,
  RQUseMutationOptions,
  RQUseMutationResult,
  RQUseQueryOptions,
} from './apiTypes/query';
import { RepetitionType } from './apiTypes/recurring';
import { FilterKeys } from './apiTypes/users';
import * as api from './requests';
import { getSortParams } from './utils/getSortParam';
import { removeObjectEmptyArrayValues } from './utils/removeObjectEmptyArrayValues';
import { removeObjectUndefinedNullValues } from './utils/removeObjectUndefinedNullValues';

const ASSESSMENTS_URL = '/assessment/';
const EXPORT_ASSESSMENTS_URL = '/export/assessment';
const DEFAULT_PAGE_SIZE = 50;

export function getAssessments({
  filters,
  page_number = 1,
  page_size = DEFAULT_PAGE_SIZE,
  school_id,
  sort = [{ columnTextId: 'name', direction: SORT_DIRECTION.ASC }],
  query,
}: ListAssessmentsForSchool): Promise<PagedResponse<Assessment>> {
  const dateFrom = filters?.date?.[0];
  const dateTo = filters?.date?.[1] || filters?.date?.[0];

  const params = removeObjectUndefinedNullValues({
    page_number,
    page_size,
    assessment_date_from: dateFrom,
    assessment_date_to: dateTo,
    search_query: query,
    sort_by: getSortParams(sort),
    group_ids: filters?.group?.join(','),
    subject_ids: filters?.subject?.join(','),
    staff_ids: filters?.staff?.join(','),
    recurrence_ids: filters?.recurrence_id?.[0],
    assessment_statuses: filters?.assessment_status?.join(','),
    report_ids: filters?.report?.join(','),
    repetition_types: filters?.repetition_type?.join(','),
    area_of_learning_ids: filters?.area_of_learning?.join(','),
    evaluation_types: filters?.evaluation_type?.join(','),
    assessment_ids: filters?.assessment_ids?.length ? filters.assessment_ids.join(',') : undefined,
    age_group_ids: filters?.age_group?.length ? filters.age_group.join(',') : undefined,
    group_types: filters?.group_types?.join(','),
    students_of_age_group_ids: filters?.students_of_age_group_ids?.join(','),
    students_of_group_ids: filters?.students_of_group_ids?.join(','),
    with_groups_only: filters?.with_groups_only?.[0],
  });

  return api.get(`${SCHOOLY_API_V2_URL}${getAssessmentsListPath({ school_id: school_id })}`, {
    params,
  });
}

export const GET_ASSESSMENTS_QUERY = `${ASSESSMENTS_URL}GET_ASSESSMENTS_QUERY`;

export const ASSESSMENTS_FILTER_KEYS = [
  FilterKeys.Date,
  FilterKeys.EvaluationType,
  FilterKeys.AreaOfLearning,
  FilterKeys.Group,
  FilterKeys.Report,
  FilterKeys.AssessmentStatus,
  FilterKeys.Subject,
  FilterKeys.Staff,
  FilterKeys.RecurrenceId,
  FilterKeys.RepetitionType,
  FilterKeys.Assessment,
] as const;

export const MARKBOOK_FILTER_KEYS = [
  FilterKeys.Date,
  FilterKeys.AgeGroup,
  FilterKeys.Group,
  FilterKeys.Subject,
  FilterKeys.AreaOfLearning,
  FilterKeys.EvaluationType,
  FilterKeys.Assessment,
  FilterKeys.GroupTypes,
] as const;

export type GetAssessmentsQueryFilters = {
  [FilterKeys.AssessmentStatus]?: AssessmentStatuses[];
  [FilterKeys.Date]?: string[];
  [FilterKeys.EvaluationType]?: EvaluationType[];
  [FilterKeys.AreaOfLearning]?: string[];
  [FilterKeys.Report]?: string[];
  [FilterKeys.Staff]?: string[];
  [FilterKeys.Group]?: string[];
  [FilterKeys.Subject]?: string[];
  [FilterKeys.RecurrenceId]?: string[];
  [FilterKeys.RepetitionType]?: RepetitionType[];
  [FilterKeys.Assessment]?: string[];
};

export type GetAssessmentsQuerySort = {
  columnTextId:
    | 'name'
    | 'assessment_date'
    | 'assessment_status'
    | 'area_of_learning_name'
    | 'evaluation_type'
    | 'publish_date';
  direction: SORT_DIRECTION;
};

export const useGetAssessmentsQuery = (
  initialParams: Omit<ListAssessmentsForSchool, 'filters' | 'sort'> & {
    filters: GetAssessmentsQueryFilters;
    sort?: GetAssessmentsQuerySort[];
  },
  options?: RQUseInfiniteQueryOptions<PagedResponse<Assessment>>,
) => {
  const [params, setParams] = useState(initialParams);

  const query = useInfiniteQuery<PagedResponse<Assessment>, ApiError>(
    [GET_ASSESSMENTS_QUERY, { ...params, filters: removeObjectEmptyArrayValues(params.filters) }],
    ({ pageParam }) =>
      getAssessments({
        page_number: pageParam,
        ...params,
        filters: removeObjectEmptyArrayValues(params.filters),
      }),
    {
      getNextPageParam: (lastPage) => {
        return !lastPage?.total_pages || lastPage?.current_page === lastPage?.total_pages
          ? undefined
          : lastPage.next_page;
      },
      getPreviousPageParam: (firstPage) => {
        return firstPage?.current_page ? firstPage?.previous_page : undefined;
      },
      ...options,
    },
  );

  return { ...query, setParams, params };
};

export const CREATE_ASSESSMENT_MUTATION = `${ASSESSMENTS_URL}CREATE_ASSESSMENT_MUTATION`;

export function createAssessment({
  schoolId,
  assessment,
}: AssessmentCreateRequest): Promise<AssessmentCreateResponse> {
  return api.post(`${ASSESSMENTS_URL}for-school/${schoolId}`, assessment);
}

export const useCreateAssessmentMutation = (
  options?: RQUseMutationOptions<AssessmentCreateResponse, AssessmentCreateRequest>,
): RQUseMutationResult<AssessmentCreateResponse, AssessmentCreateRequest> => {
  return useMutation(
    [CREATE_ASSESSMENT_MUTATION],
    (params: AssessmentCreateRequest) => createAssessment(params),
    {
      ...options,
    },
  );
};

export const UPDATE_ASSESSMENT_MUTATION = `${ASSESSMENTS_URL}UPDATE_ASSESSMENT_MUTATION`;

export function updateAssessment({
  assessmentId,
  assessment,
  confirmed,
  withFollowing,
}: AssessmentUpdateRequest): Promise<AssessmentUpdateResponse> {
  const withFollowingParam = withFollowing !== undefined ? `&with_following=${withFollowing}` : '';
  return api.patch(
    `${ASSESSMENTS_URL}${assessmentId}?confirmed=${confirmed}${withFollowingParam}`,
    assessment,
  );
}

export const useUpdateAssessmentMutation = (
  options?: RQUseMutationOptions<AssessmentUpdateResponse, AssessmentUpdateRequest>,
): RQUseMutationResult<AssessmentUpdateResponse, AssessmentUpdateRequest> => {
  return useMutation(
    [UPDATE_ASSESSMENT_MUTATION],
    (params: AssessmentUpdateRequest) => updateAssessment(params),
    options,
  );
};

export function getAssessment({ id }: GetAssessmentRequest): Promise<Assessment> {
  return api.get(`${SCHOOLY_API_V2_URL}${getSingleAssessmentPath({ assessment_id: id })}`);
}

export const GET_ASSESSMENT_QUERY = `${ASSESSMENTS_URL}/GET_ASSESSMENT_QUERY`;

export const useGetAssessmentQuery = (
  params: GetAssessmentRequest,
  options?: RQUseQueryOptions<Assessment>,
) => {
  return useQuery<Assessment, ApiError>(
    [GET_ASSESSMENT_QUERY, params],
    () => getAssessment(params),
    {
      ...options,
    },
  );
};

export type AssessmentGroupsQueryFilters = {
  [FilterKeys.AgeGroup]?: string[];
  [FilterKeys.StaffHouse]?: string[];
  [FilterKeys.StudentHouse]?: string[];
  [FilterKeys.StaffStatus]?: string[];
  [FilterKeys.StudentStatus]?: string[];
  [FilterKeys.OnlyTutorGroups]?: string[];
  [FilterKeys.Subject]?: string[];
};

export function getAssessmentGroups({
  assessment_id,
  filters: { only_tutor_groups, ...filters },
  pageNumber = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  query,
}: GetAssessmentGroupsRequest): Promise<PagedResponse<Group>> {
  const onlyTutorGroups = only_tutor_groups?.join('') === '1';

  const params = removeObjectUndefinedNullValues({
    page_number: pageNumber,
    page_size: pageSize,
    age_group: filters?.age_group?.join(','),
    subject: onlyTutorGroups ? undefined : filters?.subject?.join(','),
    group_type: onlyTutorGroups ? GroupType.TutorGroup : undefined,
    student_status: filters.student_status?.join(','),
    student_house: filters.student_house?.join(','),
    staff_status: filters.staff_status?.join(','),
    staff_house: filters.staff_house?.join(','),
    query,
  });

  return api.get(`${ASSESSMENTS_URL}${assessment_id}/groups`, {
    params,
  });
}

export const GET_ASSESSMENT_GROUPS_QUERY = `${ASSESSMENTS_URL}/GET_ASSESSMENT_GROUPS_QUERY`;

export const useGetAssessmentGroupsQuery = (
  initialParams: GetAssessmentGroupsRequest,
  options?: RQUseInfiniteQueryOptions<PagedResponse<Group>>,
) => {
  const [params, setParams] = useState(initialParams);

  const query = useInfiniteQuery<PagedResponse<Group>, ApiError>(
    [GET_ASSESSMENT_GROUPS_QUERY, params, JSON.stringify(params.filters)],
    ({ pageParam }) => getAssessmentGroups({ pageNumber: pageParam, ...params }),
    {
      getNextPageParam: (lastPage) => {
        return !lastPage?.total_pages || lastPage?.current_page === lastPage?.total_pages
          ? undefined
          : lastPage.next_page;
      },
      getPreviousPageParam: (firstPage) => {
        return firstPage?.current_page ? firstPage?.previous_page : undefined;
      },
      ...options,
    },
  );

  return { ...query, setParams, params };
};

export const OnAssessmentActionEventType = 'OnAssessmentActionEvent';

export class OnAssessmentActionEvent extends Event {
  id: Assessment['id'];
  action: AssessmentAction;

  constructor(id: Assessment['id'], action: AssessmentAction, eventInitDict?: EventInit) {
    super(OnAssessmentActionEventType, eventInitDict);

    this.id = id;
    this.action = action;
  }
}

export const PERFORM_ASSESSMENT_ACTION_MUTATION = `${ASSESSMENTS_URL}PERFORM_ASSESSMENT_ACTION_MUTATION`;

export function performAssessmentAction({
  id,
  action,
}: PerformAssessmentActionRequest): Promise<PerformAssessmentActionResponse> {
  return api.post(`${ASSESSMENTS_URL}${id}/action`, { action });
}

export const usePerformAssessmentActionMutation = (
  options?: RQUseMutationOptions<PerformAssessmentActionResponse, PerformAssessmentActionRequest>,
): RQUseMutationResult<PerformAssessmentActionResponse, PerformAssessmentActionRequest> => {
  return useMutation([PERFORM_ASSESSMENT_ACTION_MUTATION], performAssessmentAction, {
    ...options,
  });
};

type GetAssessmentsGradesForSchoolRequest = {
  schoolId: string;
  listIds?: string[];
  pageSize?: number;
  pageNumber?: number;
};

export function getAssessmentsGradesForSchool({
  schoolId,
  listIds,
  pageSize,
  pageNumber,
}: GetAssessmentsGradesForSchoolRequest): Promise<SelectListListResponseBody> {
  return api.get(getGradesPath({ school_id: schoolId }), {
    params: {
      page_size: pageSize,
      page_number: pageNumber,
      select_lists: listIds?.length ? listIds?.join(',') : undefined,
    },
  });
}

export const GET_ASSESSMENT_GRADES_FOR_SCHOOL_QUERY = `${ASSESSMENTS_URL}GET_ASSESSMENT_GRADES_FOR_SCHOOL_QUERY`;

export const useGetAssessmentsGradesForSchoolQuery = (
  params: GetAssessmentsGradesForSchoolRequest,
  options?: RQUseQueryOptions<SelectListListResponseBody>,
) => {
  const queryClient = useQueryClient();

  return useQuery<SelectListListResponseBody, ApiError>(
    [GET_ASSESSMENT_GRADES_FOR_SCHOOL_QUERY, params],
    () => getAssessmentsGradesForSchool(params),
    {
      ...options,
      onSuccess: (data) => {
        for (const grade of data) {
          queryClient.setQueryData([GET_ASSESSMENT_GRADE_QUERY, grade.id], grade);
        }
      },
    },
  );
};

export function getAssessmentsGrade(listId: string): Promise<SelectListDetailResponseBody> {
  return api.get(getSingleGradePath({ list_id: listId }));
}

export const GET_ASSESSMENT_GRADE_QUERY = `${ASSESSMENTS_URL}GET_ASSESSMENT_GRADE_QUERY`;

export const useGetAssessmentGradeQuery = (
  id: string,
  options?: RQUseQueryOptions<SelectListDetailResponseBody>,
) => {
  return useQuery<SelectListDetailResponseBody, ApiError>(
    [GET_ASSESSMENT_GRADE_QUERY, id],
    () => getAssessmentsGrade(id),
    {
      ...options,
    },
  );
};

export function createAssessmentsGrade({
  schoolId,
  grade,
}: CreateAssessmentGradeRequest): Promise<SelectListCreateResponse> {
  return api.post(createGradePath({ school_id: schoolId }), grade);
}

export const useCreateAssessmentsGradeMutation = (
  options?: RQUseMutationOptions<SelectListCreateResponse, CreateAssessmentGradeRequest>,
): RQUseMutationResult<SelectListCreateResponse, CreateAssessmentGradeRequest> => {
  return useMutation((params: CreateAssessmentGradeRequest) => createAssessmentsGrade(params), {
    ...options,
  });
};

export function updateAssessmentsGrade({
  listId,
  grade,
}: UpdateAssessmentGradeRequest): Promise<SelectListPartialUpdateResponse> {
  return api.patch(updateGradePath({ list_id: listId }), grade);
}

export const useUpdateAssessmentsGradeMutation = (
  options?: RQUseMutationOptions<SelectListPartialUpdateResponse, UpdateAssessmentGradeRequest>,
): RQUseMutationResult<SelectListPartialUpdateResponse, UpdateAssessmentGradeRequest> => {
  return useMutation((params: UpdateAssessmentGradeRequest) => updateAssessmentsGrade(params), {
    ...options,
  });
};

export function archiveAssessmentsGrade(listId: string): Promise<SelectListDeleteResponse> {
  return api.remove(deleteGradePath({ list_id: listId }));
}

export const useArchiveAssessmentsGradeMutation = (
  options?: RQUseMutationOptions<SelectListDeleteResponse, string>,
): RQUseMutationResult<SelectListDeleteResponse, string> => {
  return useMutation((listId: string) => archiveAssessmentsGrade(listId), {
    ...options,
  });
};

interface UpsertDeleteAssessmentEntryRequest {
  method_id: string;
  recipient_relation_id: string;
  data: AssessmentEntrySave;
}
interface UpsertDeleteAssessmentEntryResponse {
  success: string;
  assessment_entry?: UpsertDeleteAssessmentEntry;
}

export const UPSERT_DELETE_ASSESSMENT_ENTRY_MUTATION = `${ASSESSMENTS_URL}UPSERT_DELETE_ASSESSMENT_ENTRY_MUTATION`;

export function upsertDeleteAssessmentEntry({
  method_id,
  recipient_relation_id,
  data,
}: UpsertDeleteAssessmentEntryRequest): Promise<UpsertDeleteAssessmentEntryResponse> {
  return api.post(
    `${ASSESSMENTS_URL}entries/for-method/${method_id}/for-relation/${recipient_relation_id}`,
    { ...data },
  );
}

export const useUpsertDeleteAssessmentEntryMutation = (
  options?: RQUseMutationOptions<
    UpsertDeleteAssessmentEntryResponse,
    UpsertDeleteAssessmentEntryRequest
  >,
): RQUseMutationResult<UpsertDeleteAssessmentEntryResponse, UpsertDeleteAssessmentEntryRequest> => {
  return useMutation(
    [UPSERT_DELETE_ASSESSMENT_ENTRY_MUTATION],
    (params: UpsertDeleteAssessmentEntryRequest) => upsertDeleteAssessmentEntry(params),
    {
      ...options,
    },
  );
};

export const DELETE_ASSESSMENT_MUTATION = `${ASSESSMENTS_URL}DELETE_ASSESSMENT_MUTATION`;

export function deleteAssessment({
  assessmentId,
  confirmed,
  withFollowing,
}: AssessmentDeleteRequest): Promise<AssessmentDeleteResponse> {
  const withFollowingParam = withFollowing !== undefined ? `&with_following=${withFollowing}` : '';
  return api.remove(
    `${ASSESSMENTS_URL}${assessmentId}?confirmed=${confirmed}${withFollowingParam}`,
  );
}

export const useDeleteAssessmentMutation = (
  options?: RQUseMutationOptions<AssessmentDeleteResponse, AssessmentDeleteRequest>,
): RQUseMutationResult<AssessmentDeleteResponse, AssessmentDeleteRequest> => {
  return useMutation(
    [DELETE_ASSESSMENT_MUTATION],
    (params: AssessmentDeleteRequest) => deleteAssessment(params),
    {
      ...options,
    },
  );
};

export const exportAssessmentForGroup = ({
  groupId,
  date,
  assessmentIds,
}: AssessmentForGroupExportRequest) => {
  const params = removeObjectUndefinedNullValues({
    date_from: date[0],
    date_to: date[1],
    assessment_ids: assessmentIds?.length ? assessmentIds.join(',') : undefined,
  });

  return api.get(`${EXPORT_ASSESSMENTS_URL}/for-group/${groupId}`, {
    responseType: 'arraybuffer',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/csv',
    },
    params,
  });
};

export const useExportAssessmentForGroupMutation = (
  options?: RQUseMutationOptions<ArrayBuffer, AssessmentForGroupExportRequest>,
): RQUseMutationResult<ArrayBuffer, AssessmentForGroupExportRequest> => {
  return useMutation(
    (params: AssessmentForGroupExportRequest) => exportAssessmentForGroup(params),
    options,
  );
};

export const exportAssessmentForRelation = ({
  relationId,
  schoolYearIds,
}: AssessmentForRelationExportRequest) => {
  return api.get(`${EXPORT_ASSESSMENTS_URL}/for-relation/${relationId}`, {
    responseType: 'arraybuffer',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/csv',
    },
    params: {
      school_year_ids: schoolYearIds.join(','),
    },
  });
};

export const useExportAssessmentForRelationMutation = (
  options?: RQUseMutationOptions<ArrayBuffer, AssessmentForRelationExportRequest>,
): RQUseMutationResult<ArrayBuffer, AssessmentForRelationExportRequest> => {
  return useMutation(
    (params: AssessmentForRelationExportRequest) => exportAssessmentForRelation(params),
    options,
  );
};

export const exportAssessmentSingle = ({
  assessmentId,
  groupIds,
}: AssessmentSingleExportRequest) => {
  return api.get(`${EXPORT_ASSESSMENTS_URL}/single/${assessmentId}`, {
    responseType: 'arraybuffer',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/csv',
    },
    params: {
      group_ids: groupIds.join(','),
    },
  });
};

export const useExportAssessmentSingleMutation = (
  options?: RQUseMutationOptions<ArrayBuffer, AssessmentSingleExportRequest>,
): RQUseMutationResult<ArrayBuffer, AssessmentSingleExportRequest> => {
  return useMutation(
    (params: AssessmentSingleExportRequest) => exportAssessmentSingle(params),
    options,
  );
};

export type GetAssessmentsEntriesFilters = {
  [FilterKeys.Assessment]?: string[];
  [FilterKeys.Relation]?: string[];
  [FilterKeys.Date]?: string[];
  [FilterKeys.Subject]?: string[];
  [FilterKeys.Report]?: string[];
  [FilterKeys.Group]?: string[];
};

export type GetAssessmentsEntriesSort = {
  columnTextId:
    | 'name'
    | 'subject_order'
    | 'group_name'
    | 'evaluation_type'
    | 'area_of_learning_name'
    | 'assessment_date'
    | 'group_type';
  direction: SORT_DIRECTION;
};

export const getAssessmentsEntries = ({
  schoolId,
  query,
  sort = [{ columnTextId: 'name', direction: SORT_DIRECTION.ASC }],
  filters,
  pageSize,
  pageNumber,
}: GetAssessmentsEntriesParams): Promise<PagedResponse<AssessmentWithEntries>> => {
  const params = removeObjectUndefinedNullValues({
    page_size: pageSize,
    page_number: pageNumber,
    sort_by: getSortParams(sort),
    subject_ids: filters?.subject?.length ? filters.subject.join(',') : undefined,
    report_ids: filters?.report?.length ? filters.report.join(',') : undefined,
    group_ids: filters?.group?.length ? filters.group.join(',') : undefined,
    assessment_ids: filters?.assessment_ids?.length ? filters.assessment_ids.join(',') : undefined,
    relation_ids: filters?.relation_ids?.length ? filters.relation_ids.join(',') : undefined,
    assessment_date_from: filters?.date?.[0],
    assessment_date_to: filters?.date?.[1] || filters?.date?.[0],
    search_query: query || undefined,
  });

  return api.get(
    `${SCHOOLY_API_V2_URL}${getAssessmentEntriesForSchoolPath({ school_id: schoolId })}`,
    { params },
  );
};

export const GET_ASSESSMENTS_ENTRIES_QUERY = `${ASSESSMENTS_URL}GET_ASSESSMENTS_ENTRIES_QUERY`;

export const useGetAssessmentsEntriesQuery = (
  initialParams: GetAssessmentsEntriesParams,
  options?: RQUseInfiniteQueryOptions<PagedResponse<AssessmentWithEntries>>,
) => {
  const [params, setParams] = useState(initialParams);

  const filters = removeObjectEmptyArrayValues(params.filters ?? {});

  const query = useInfiniteQuery<PagedResponse<AssessmentWithEntries>, ApiError>(
    [GET_ASSESSMENTS_ENTRIES_QUERY, { ...params, filters }],
    ({ pageParam }) => getAssessmentsEntries({ pageNumber: pageParam, ...params, filters }),
    {
      getNextPageParam: (lastPage) => {
        return !lastPage.total_pages || lastPage.current_page === lastPage.total_pages
          ? undefined
          : lastPage.next_page;
      },
      getPreviousPageParam: (firstPage) => {
        return firstPage.current_page ? firstPage.previous_page : undefined;
      },
      ...options,
    },
  );

  return { ...query, setParams, params };
};

export type GetMarkbookFilters = {
  [FilterKeys.Date]?: string[];
  [FilterKeys.Assessment]?: string[];
  [FilterKeys.Group]?: string[];
  [FilterKeys.AgeGroup]?: string[];
  [FilterKeys.Subject]?: string[];
  [FilterKeys.GroupTypes]?: number[];
  [FilterKeys.MethodTypes]?: number[];
};

export const getMarkbook = ({
  schoolId,
  query,
  sort = [
    { sortBy: GetMarkbookSortBy.LastName, direction: SORT_DIRECTION.ASC },
    { sortBy: GetMarkbookSortBy.GivenName, direction: SORT_DIRECTION.ASC },
  ],
  filters,
  pageSize,
  pageNumber,
  arrangeBy,
}: GetMarkbookParams): Promise<GetMarkbookResponseBody> => {
  const transformedSort = transformMarkbookSortParams(sort);

  const params = removeObjectUndefinedNullValues({
    page_size: pageSize,
    page_number: pageNumber,
    sort_by: getSortParams(transformedSort),
    search_query: query || undefined,
    date_from: filters?.date?.[0],
    date_to: filters?.date?.[1] || filters?.date?.[0],
    method_types: filters?.method_types?.length ? filters.method_types.join(',') : undefined,
    assessment_ids: filters?.assessment_ids?.length ? filters.assessment_ids.join(',') : undefined,
    age_group_ids: filters?.age_group?.length ? filters.age_group.join(',') : undefined,
    group_ids: filters?.group?.length ? filters.group.join(',') : undefined,
    arrange_by: arrangeBy,
    subject_ids: filters?.subject?.length ? filters.subject.join(',') : undefined,
    group_types: filters?.group_types?.length ? filters.group_types.join(',') : undefined,
  });

  return api.get(`${SCHOOLY_API_V2_URL}${getMarkbookPath({ school_id: schoolId })}`, { params });
};

export const transformMarkbookSortParams = (sort: GetMarkbookSort[]): IColumnSort[] => {
  return sort.map((s) => {
    if (isGetMarkbookSortWithParams(s)) {
      return {
        columnTextId: `${s.sortBy}_${s.assessmentId}_${s.subjectId}`,
        direction: s.direction,
      };
    }

    return { columnTextId: s.sortBy, direction: s.direction };
  });
};

export const GET_MARKBOOK_QUERY = `${ASSESSMENTS_URL}GET_MARKBOOK_QUERY`;

export const useGetMarkbookQuery = (
  initialParams: GetMarkbookParams,
  options?: RQUseQueryOptions<GetMarkbookResponseBody>,
) => {
  const [params, setParams] = useState(initialParams);

  const filters = removeObjectEmptyArrayValues(params.filters ?? {});

  const query = useQuery<GetMarkbookResponseBody, ApiError>(
    [GET_MARKBOOK_QUERY, { ...params, filters }],
    () => getMarkbook({ ...params, filters }),
    options,
  );

  return { ...query, setParams, params };
};

export const useGetMarkbookInfiniteQuery = (
  initialParams: GetMarkbookParams,
  options?: RQUseInfiniteQueryOptions<GetMarkbookResponseBody>,
) => {
  // biome-ignore lint/correctness/useHookAtTopLevel: Legacy code
  const [params, setParams] = useState(initialParams);

  const filters = removeObjectEmptyArrayValues(params.filters ?? {});

  // biome-ignore lint/correctness/useHookAtTopLevel: Legacy code
  const query = useInfiniteQuery<GetMarkbookResponseBody, ApiError>(
    [GET_MARKBOOK_QUERY, { ...params, filters }],
    ({ pageParam }) => getMarkbook({ ...params, pageNumber: pageParam, filters }),
    {
      getNextPageParam: (res) => {
        const lastPage = res?.[0]?.students;
        if (!lastPage) return undefined;

        return !lastPage.total_pages || lastPage.current_page === lastPage.total_pages
          ? undefined
          : lastPage.next_page;
      },
      getPreviousPageParam: (res) => {
        const firstPage = res?.[0]?.students;
        if (!firstPage) return undefined;
        return firstPage.current_page ? firstPage.previous_page : undefined;
      },
      ...options,
    },
  );

  return { ...query, setParams, params };
};

export type UseGetMarkbookQueriesParams = {
  id: string;
  params: GetMarkbookParams;
};

/**
 * @warning Use stable params that don't change length during the component's lifecycle
 *
 * This hook calls useGetMarkbookInfiniteQuery inside a forEach loop.
 * When the params array changes length between renders,
 * it causes a different number of hooks to be called, leading to:
 * "React has detected a change in the order of Hooks called"
 */
export const useGetMarkbookQueries = (
  params: UseGetMarkbookQueriesParams[],
  options?: RQUseInfiniteQueryOptions<GetMarkbookResponseBody>,
) => {
  const queries = new Map<string, ReturnType<typeof useGetMarkbookInfiniteQuery>>();
  params.forEach(({ id, params }) => {
    // biome-ignore lint/correctness/useHookAtTopLevel: Legacy code
    queries.set(id, useGetMarkbookInfiniteQuery(params, options));
  });

  const fetchNextPage = useCallback(
    (id: string) => {
      const query = queries.get(id);
      if (!query) return;

      query.fetchNextPage();
    },
    [queries],
  );

  const refetch = useCallback(
    (id: string) => {
      const query = queries.get(id);
      if (!query) return;
      query.refetch();
    },
    [queries],
  );

  const hasNextPage = useCallback(
    (id: string) => {
      const query = queries.get(id);
      if (!query) return false;

      return query.hasNextPage ?? false;
    },
    [queries],
  );

  const isFetchingNextPage = useCallback(
    (id: string) => {
      const query = queries.get(id);
      if (!query) return false;

      return query.isFetchingNextPage;
    },
    [queries],
  );

  const setParams = useCallback(
    (id: string, params: GetMarkbookParams) => {
      const query = queries.get(id);
      if (!query) return;

      query.setParams(params);
    },
    [queries],
  );

  return { queries, fetchNextPage, refetch, hasNextPage, isFetchingNextPage, setParams };
};

export type ExportMarkbookFilters = GetMarkbookFilters & {
  [FilterKeys.ConductType]?: string[];
};

export const exportMarkbook = ({
  schoolId,
  filters,
  sort = [
    { sortBy: GetMarkbookSortBy.LastName, direction: SORT_DIRECTION.ASC },
    { sortBy: GetMarkbookSortBy.GivenName, direction: SORT_DIRECTION.ASC },
  ],
  query,
  arrangeAssessmentsBy,
  arrangeStudentsBy,
  include,
}: ExportMarkbookParams): Promise<ArrayBuffer> => {
  const transformedSort = transformMarkbookSortParams(sort);

  const params = removeObjectUndefinedNullValues({
    include: include.join(','),
    arrange_assessments_by: arrangeAssessmentsBy,
    arrange_students_by: arrangeStudentsBy,
    sort_by: getSortParams(transformedSort),
    search_query: query || undefined,
    date_from: filters?.date?.[0],
    date_to: filters?.date?.[1] || filters?.date?.[0],
    assessment_ids: filters?.assessment_ids?.length ? filters.assessment_ids.join(',') : undefined,
    age_group_ids: filters?.age_group?.length ? filters.age_group.join(',') : undefined,
    group_ids: filters?.group?.length ? filters.group.join(',') : undefined,
    subject_ids: filters?.subject?.length ? filters.subject.join(',') : undefined,
    group_types: filters?.group_types?.length ? filters.group_types.join(',') : undefined,
    conduct_type_ids: filters?.conduct_type?.length ? filters.conduct_type.join(',') : undefined,
  });

  return api.get(`${SCHOOLY_API_V2_URL}${exportMarkbookPath({ school_id: schoolId })}`, {
    responseType: 'arraybuffer',
    headers: { 'Content-Type': 'application/json', Accept: 'application/csv' },
    params,
  });
};

export const useExportMarkbook = (
  options?: RQUseMutationOptions<ArrayBuffer, ExportMarkbookParams>,
): RQUseMutationResult<ArrayBuffer, ExportMarkbookParams> => {
  return useMutation(
    (params) =>
      exportMarkbook({ ...params, filters: removeObjectEmptyArrayValues(params.filters ?? {}) }),
    options,
  );
};

export type GetAssessmentsAverageParams = {
  query: GetAssessmentsAverageRequestQuery;
  params: GetAssessmentsAverageRequestParams;
};

const getAssessmentsAverage = ({
  query,
  params,
}: GetAssessmentsAverageParams): Promise<GetAssessmentsAverageResponse> => {
  return api.get(
    `${SCHOOLY_API_V2_URL}${buildAssessmentsAveragePath({ school_id: params.schoolId })}`,
    {
      params: {
        ...query,
        sort_by:
          query.sort_by ??
          getSortParams([{ columnTextId: 'assessment_name', direction: SORT_DIRECTION.ASC }]),
      },
    },
  );
};

export const GET_ASSESSMENTS_AVERAGE_QUERY = `${ASSESSMENTS_URL}GET_ASSESSMENTS_AVERAGE_QUERY`;

export const useGetAssessmentsAverage = (
  params: GetAssessmentsAverageParams,
  options?: RQUseQueryOptions<GetAssessmentsAverageResponse>,
) => {
  return useQuery<GetAssessmentsAverageResponse, ApiError>(
    [GET_ASSESSMENTS_AVERAGE_QUERY, params],
    () => getAssessmentsAverage(params),
    {
      ...options,
    },
  );
};
